// Pure helpers for first-party vendor selection on the order form.
// Kept free of react-native imports so they can be unit-tested directly.

// Distinct first-party vendors (with a non-blank prefix) drawn from a product
// list, sorted by id. The first entry is the default selection.
export const firstPartyVendorsFromProducts = (products = []) => {
  const byId = new Map();

  products.forEach(product => {
    const vendor = product.vendor;

    if (vendor?.firstParty && vendor.prefix && !byId.has(vendor.id)) {
      byId.set(vendor.id, vendor);
    }
  });

  return [...byId.values()].sort((a, b) => (
    String(a.id).localeCompare(String(b.id), undefined, { numeric: true })
  ));
};

// Orders for a product, preferring those still visible to the dealer.
const productOrders = ({ dealer, product }) => {
  const visible = dealer.orders.filter(order => (
    order.product.id === product.id && !order.hiddenFromDealerAt
  ));

  if (visible.length) { return visible; }

  return dealer.orders.filter(order => order.product.id === product.id);
};

// Resolve the "Last Order" match for a product card, in priority order:
//   1. a direct order for this product,
//   2. an order for a product this one supersedes ("Old Style"),
//   3. an order for another first-party vendor's equivalently-named product.
// Returns the matching dealerProduct plus flags describing which case hit.
export const resolveLastOrderMatch = ({ product, dealer, allProducts = [] }) => {
  let match = dealer.dealerProducts.find(dealerProduct => (
    dealerProduct.product.id === product.id &&
    productOrders({ dealer, product: dealerProduct.product }).length > 0
  ));
  let isSuggested = false;
  let crossVendorName = null;

  if (!match) {
    match = dealer.dealerProducts.find(dealerProduct => (
      dealerProduct.suggestedProduct?.id === product.id &&
      productOrders({ dealer, product: dealerProduct.product }).length > 0
    ));

    isSuggested = !!match;
  }

  if (!match) {
    const equivalentMatch = allProducts
      .filter(other => (
        other.name === product.name &&
        other.vendor?.firstParty &&
        other.vendor?.id !== product.vendor?.id
      ))
      .map(equivalent => ({
        equivalent,
        dealerProduct: dealer.dealerProducts.find(dealerProduct => (
          dealerProduct.product.id === equivalent.id &&
          productOrders({ dealer, product: dealerProduct.product }).length > 0
        )),
      }))
      .find(entry => entry.dealerProduct);

    if (equivalentMatch) {
      match = equivalentMatch.dealerProduct;
      crossVendorName = equivalentMatch.equivalent.vendor.name;
    }
  }

  const orders = match ?
    productOrders({ dealer, product: match.product }) : [];
  const lastOrder = match ? orders[orders.length - 1] : null;

  return { match, isSuggested, crossVendorName, lastOrder };
};
